import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, X, ImagePlus } from "lucide-react";
import { API, authHeader, fileToCompressedDataURL } from "../../lib/adminApi";
import { useContent } from "../../context/ContentContext";

// Generic add/edit/delete editor for a list collection.
// props: collection, title, subtitle, fields:[{name,label,type,placeholder}], titleField
export default function ListTab({ collection, title, subtitle, fields, titleField }) {
  const { content, refresh } = useContent();
  const items = content[collection] || [];
  const [editing, setEditing] = useState(null); // object or null
  const [saving, setSaving] = useState(false);

  const blank = () => {
    const o = {};
    fields.forEach((f) => (o[f.name] = f.type === "toggle" ? true : ""));
    return o;
  };

  const startAdd = () => setEditing(blank());
  const startEdit = (item) => setEditing({ ...item });
  const cancel = () => setEditing(null);

  const onImage = async (e, name) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const dataUrl = await fileToCompressedDataURL(file);
      setEditing((prev) => ({ ...prev, [name]: dataUrl }));
    } catch {
      toast.error("Could not read image");
    }
  };

  const save = async () => {
    const tf = titleField || fields[0].name;
    if (!String(editing[tf] || "").trim()) {
      toast.error(`${fields.find((f) => f.name === tf).label} is required.`);
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/list/${collection}/${editing.id}`, editing, authHeader());
      } else {
        await axios.post(`${API}/admin/list/${collection}`, editing, authHeader());
      }
      await refresh();
      toast.success("Saved");
      setEditing(null);
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Session expired \u2014 please log in again." : "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/list/${collection}/${id}`, authHeader());
      await refresh();
      toast.success("Removed");
    } catch {
      toast.error("Could not delete");
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#D8CCB8] focus:border-[#C6A15B] outline-none py-2.5 text-[#1A1A1A]";

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-serif-display text-3xl text-[#1A1A1A]">{title}</h1>
        {!editing && (
          <button
            onClick={startAdd}
            className="flex items-center gap-2 bg-[#0C0C0C] text-[#C6A15B] text-xs tracking-[0.18em] uppercase px-5 py-3 hover:bg-[#1a1a1a] transition-colors"
          >
            <Plus size={16} /> Add
          </button>
        )}
      </div>
      {subtitle && <p className="text-[#8A857A] text-sm mb-8">{subtitle}</p>}

      {editing && (
        <div className="bg-white border border-[#E4DBCB] p-6 mb-8">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-serif-display text-xl text-[#1A1A1A]">
              {editing.id ? "Edit item" : "New item"}
            </h2>
            <button onClick={cancel} className="text-[#8A857A] hover:text-[#1A1A1A]">
              <X size={20} />
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {fields.map((f) => (
              <div key={f.name} className={f.type === "textarea" || f.type === "image" ? "md:col-span-2" : ""}>
                <label className="block text-[11px] tracking-[0.16em] uppercase text-[#8A857A] mb-1.5">
                  {f.label}
                </label>
                {f.type === "textarea" ? (
                  <textarea
                    rows={3}
                    value={editing[f.name] || ""}
                    onChange={(e) => setEditing({ ...editing, [f.name]: e.target.value })}
                    placeholder={f.placeholder}
                    className={inputCls + " resize-none"}
                  />
                ) : f.type === "toggle" ? (
                  <button
                    type="button"
                    onClick={() => setEditing({ ...editing, [f.name]: !editing[f.name] })}
                    className={`px-4 py-2 text-xs tracking-[0.16em] uppercase border ${
                      editing[f.name]
                        ? "bg-[#C6A15B] text-[#0C0C0C] border-[#C6A15B]"
                        : "text-[#8A857A] border-[#D8CCB8]"
                    }`}
                  >
                    {editing[f.name] ? "Active" : "Hidden"}
                  </button>
                ) : f.type === "image" ? (
                  <label className="block cursor-pointer border border-dashed border-[#cbbfa8] hover:border-[#C6A15B] transition-colors h-40 w-40 overflow-hidden bg-[#F7F2EA] relative">
                    {editing[f.name] ? (
                      <img src={editing[f.name]} alt={f.label} className="w-full h-full object-cover" />
                    ) : (
                      <span className="absolute inset-0 flex flex-col items-center justify-center text-[#8A857A]">
                        <ImagePlus size={24} className="mb-1 text-[#C6A15B]" />
                        <span className="text-[11px]">Upload</span>
                      </span>
                    )}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => onImage(e, f.name)} />
                  </label>
                ) : (
                  <input
                    value={editing[f.name] || ""}
                    onChange={(e) => setEditing({ ...editing, [f.name]: e.target.value })}
                    placeholder={f.placeholder}
                    className={inputCls}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-3 mt-6">
            <button
              onClick={save}
              disabled={saving}
              className="bg-[#0C0C0C] text-[#C6A15B] text-xs tracking-[0.2em] uppercase px-7 py-3 hover:bg-[#1a1a1a] transition-colors disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button onClick={cancel} className="text-xs tracking-[0.2em] uppercase text-[#8A857A] px-4">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {items.length === 0 && <p className="text-[#8A857A] text-sm">No items yet.</p>}
        {items.map((item) => {
          const imgField = fields.find((f) => f.type === "image");
          const tf = titleField || fields[0].name;
          const secondary = fields.filter((f) => f.name !== tf && f.type !== "image");
          return (
            <div key={item.id} className="bg-white border border-[#E4DBCB] p-4 flex items-center gap-4">
              {imgField &&
                (item[imgField.name] ? (
                  <img src={item[imgField.name]} alt="" className="h-14 w-14 object-cover rounded-sm shrink-0" />
                ) : (
                  <div className="h-14 w-14 bg-[#F0E9DB] shrink-0" />
                ))}
              <div className="flex-1 min-w-0">
                <div className="font-serif-display text-lg text-[#1A1A1A] truncate">{item[tf]}</div>
                <div className="text-xs text-[#8A857A] truncate">
                  {secondary.map((f) => (item[f.name] ? `${item[f.name]}` : "")).filter(Boolean).join("  \u00b7  ")}
                </div>
              </div>
              <button onClick={() => startEdit(item)} className="text-[#C6A15B] hover:text-[#a9863f] shrink-0 p-2" aria-label="Edit">
                <Pencil size={17} />
              </button>
              <button onClick={() => remove(item.id)} className="text-[#b04a3f] hover:text-[#7f322a] shrink-0 p-2" aria-label="Delete">
                <Trash2 size={17} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
